/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 */
import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {getUrl} from '../../../utils/cloudinary-utils'
import {KS_WISHLIST_URL, SELECTED_ATTRIBUTES_WIDGET_PRODUCTS} from '../../../constants'
import {
    Box,
    Flex,
    Heading,
    Image,
    Link,
    Stack,
    Text,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Skeleton, SkeletonText} from '@chakra-ui/react'
import {
    useProducts,
    useShopperBasketsMutationHelper,
    useShopperCustomersMutation
} from '@salesforce/commerce-sdk-react'
import {useWishList} from '@salesforce/retail-react-app/app/hooks/use-wish-list'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

import {Swiper, SwiperSlide} from 'swiper/react'
import {Navigation, FreeMode} from 'swiper/modules'
import WishlistCarouselItem from './wishlist-carousel-item'
import {WishlistGuestPrompt} from './wishlist-guest-prompt'
import useContentAsset from '../../../hooks/use-content-asset.js'
import useTagManager from '../../../hooks/use-tag-manager'
import {scheduleIdleAnalytics} from '../../../utils/common-utils'

import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/free-mode'
import {useGlobaleContext} from '../../../contexts/globale.jsx'

const MAX_CAROUSEL_ITEMS = 12
const ITEM_WIDTH_PX = 175

const WishlistEmptyStateSkeleton = () => {
    const cartStyles = useMultiStyleConfig('CartStyles')

    return (
        <Box {...cartStyles.wishlistInCart.callout} data-testid="sf-cart-wishlist-empty-skeleton">
            <Flex {...cartStyles.wishlistInCart.titleWithIcon}>
                <Skeleton borderRadius="full" height="16px" width="16px" />
                <Skeleton height="36px" width="190px" />
            </Flex>

            <SkeletonText noOfLines={2} spacing={3} skeletonHeight={4} width="100%" />
        </Box>
    )
}

const WishlistHeadingSkeleton = () => {
    const cartStyles = useMultiStyleConfig('CartStyles')

    return (
        <Flex
            {...cartStyles.wishlistInCart.titleRow}
            data-testid="sf-cart-wishlist-heading-skeleton"
        >
            <Skeleton height="36px" width="190px" />
            <Box marginTop={2}>
                <Skeleton height="18px" width="100px" />
            </Box>
        </Flex>
    )
}

const WishlistEmptyState = () => {
    const cartStyles = useMultiStyleConfig('CartStyles')
    const {data: wishlistEmptyMsg, isLoading: wishlistEmptyMsgLoading} = useContentAsset(
        'wishlist-registered-callout-msg'
    )

    if (wishlistEmptyMsgLoading) {
        return <WishlistEmptyStateSkeleton />
    }

    const message = wishlistEmptyMsg?.contentAssetHTML ? (
        <Box
            dangerouslySetInnerHTML={{__html: wishlistEmptyMsg.contentAssetHTML}}
            {...cartStyles.wishlistInCart.emptyStateAsset}
        />
    ) : (
        <>
            <Flex {...cartStyles.wishlistInCart.titleWithIcon}>
                <Box {...cartStyles.wishlistInCart.titleIcon} aria-hidden="true">
                    <Image
                        src={getUrl('UIImages/images/empty-heart-icon.svg')}
                        width="16px"
                        alt="empty heart"
                        height="16px"
                    />
                </Box>
                <Heading {...cartStyles.wishlistInCart.title}>
                    <FormattedMessage defaultMessage="Your Wishlist" id="cart.wishlist.title" />
                </Heading>
            </Flex>

            <Text {...cartStyles.wishlistInCart.bodyText}>
                <FormattedMessage
                    defaultMessage="Nothing saved yet. Heart items you love and see them appear here."
                    id="cart.wishlist.empty.message"
                />
            </Text>
        </>
    )

    return (
        <Box {...cartStyles.wishlistInCart.callout} data-testid="sf-cart-wishlist-empty">
            {/* Heading with heart icon */}
            {message}
        </Box>
    )
}

WishlistEmptyState.propTypes = {}

WishlistEmptyStateSkeleton.propTypes = {}

const WishlistItemSkeleton = () => (
    <Box flexShrink={0} width={`${ITEM_WIDTH_PX}px`}>
        <Stack spacing={2}>
            <Skeleton borderRadius="sm" height={`${ITEM_WIDTH_PX}px`} width="100%" />
            <SkeletonText noOfLines={2} spacing={1} skeletonHeight={3} />
            <Skeleton borderRadius="sm" height="28px" width="100%" />
        </Stack>
    </Box>
)

WishlistItemSkeleton.displayName = 'WishlistItemSkeleton'

WishlistCarouselItem.propTypes = {
    wishlistItem: PropTypes.shape({
        id: PropTypes.string.isRequired,
        productId: PropTypes.string.isRequired,
        quantity: PropTypes.number
    }).isRequired,
    product: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        price: PropTypes.number,
        currency: PropTypes.string,
        productPromotions: PropTypes.arrayOf(
            PropTypes.shape({
                promotionalPrice: PropTypes.number
            })
        ),
        imageGroups: PropTypes.arrayOf(
            PropTypes.shape({
                viewType: PropTypes.string,
                images: PropTypes.arrayOf(
                    PropTypes.shape({
                        link: PropTypes.string,
                        alt: PropTypes.string
                    })
                )
            })
        )
    }),
    isMoving: PropTypes.bool,
    onMoveToBag: PropTypes.func.isRequired
}

WishlistCarouselItem.defaultProps = {
    product: undefined,
    isMoving: false
}

const WishlistCarousel = ({items, movingItemIds, products, onMoveToBag}) => {
    const cartStyles = useMultiStyleConfig('CartStyles')
    const tagManager = useTagManager()
    const wishlistLength = typeof products === 'object' ? Object.values(products)?.length : 0
    useEffect(() => {
        if (wishlistLength && !window.viewListTrigerred) {
            window.__dataLayerLoaded = true
            tagManager.viewItemLists(Object.values(products), undefined, {
                method: 'cart',
                listId: 'other',
                listName: 'other'
            })
            window.viewListTrigerred = true
            window.setTimeout(() => {
                delete window.viewListTrigerred
            }, 1000)
            window.__dataLayerLoaded = false
        }
    }, [])

    return (
        <Box {...cartStyles.wishlistInCart.carouselWrapper}>
            <Swiper
                modules={[Navigation, FreeMode]}
                navigation
                slidesPerView="auto"
                spaceBetween={10}
                freeMode={{enabled: true}}
                grabCursor
                style={{width: '100%'}}
            >
                {items
                    ?.slice(0, MAX_CAROUSEL_ITEMS)
                    ?.filter((wishlistItem) => products?.[wishlistItem.productId])
                    ?.map((wishlistItem, index) => (
                        <SwiperSlide key={wishlistItem.id} style={{width: '175px'}}>
                            <WishlistCarouselItem
                                wishlistItem={wishlistItem}
                                product={products?.[wishlistItem.productId]}
                                isMoving={movingItemIds.includes(wishlistItem.id)}
                                onMoveToBag={onMoveToBag}
                                index={index}
                            />
                        </SwiperSlide>
                    ))}
            </Swiper>
        </Box>
    )
}

WishlistCarousel.propTypes = {
    items: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            productId: PropTypes.string.isRequired
        })
    ).isRequired,
    movingItemIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    products: PropTypes.object,
    onMoveToBag: PropTypes.func.isRequired
}

WishlistCarousel.defaultProps = {
    products: undefined
}

const WishlistInCart = () => {
    const cartStyles = useMultiStyleConfig('CartStyles')
    const {globaleCurrency} = useGlobaleContext()
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    const {formatMessage} = useIntl()
    const toast = useToast()

    const {
        app: {commerceAPI}
    } = getConfig()
    const {hostUrl} = commerceAPI.storefront

    const {data: wishlist, isLoading: isWishlistLoading} = useWishList()
    const {data: basket} = useCurrentBasket()
    const basketId = basket?.basketId

    const {data: customer} = useCurrentCustomer()
    const {customerId, isRegistered} = customer ?? {}

    const [movingItemIds, setMovingItemIds] = useState([])
    const [removedItemIds, setRemovedItemIds] = useState([])
    const tagManager = useTagManager()

    const allWishlistItems = wishlist?.customerProductListItems ?? []
    const visibleItems = allWishlistItems
        .filter((item) => !removedItemIds.includes(item.id))
        .slice(0, MAX_CAROUSEL_ITEMS)

    const productIds = visibleItems.map(({productId}) => productId).join(',')
    const {data: wishlistCarouselHeading, isLoading: wishlistCarouselHeadingLoading} =
        useContentAsset('wishlist-cart-carousel-heading')
    const selectedAttributes = SELECTED_ATTRIBUTES_WIDGET_PRODUCTS
    const {data: products, isLoading: isProductsLoading} = useProducts(
        {
            parameters: {
                ids: productIds,
                allImages: true,
                perPricebook: true,
                allVariationProperties: true,
                select: selectedAttributes,
                expand: 'availability,promotions,images,prices,variations,links'
            }
        },
        {
            enabled: Boolean(productIds),
            select: (result) =>
                result?.data?.reduce((acc, item) => {
                    acc[item.id] = item
                    return acc
                }, {})
        }
    )

    const deleteWishlistItem = useShopperCustomersMutation('deleteCustomerProductListItem')

    const showError = (messageOverride) =>
        toast({
            title: messageOverride ?? formatMessage(API_ERROR_MESSAGE),
            status: 'error',
            duration: 4000,
            isClosable: true
        })

    const handleMoveToBag = async (wishlistItem, pwaProduct, productIndex) => {
        if (movingItemIds.includes(wishlistItem.id)) return

        setMovingItemIds((prev) => [...prev, wishlistItem.id])
        if (basket?.productItems?.length || basket?.giftCertificateItems?.length) {
            tagManager.ctaClick(
                'cta_click',
                'wishlist',
                'move to bag',
                'cart wishlist',
                'move to bag'
            )
        } else {
            tagManager.ctaClick(
                'cta_click',
                'Wishlist Interaction',
                'cta click - Move to Bag',
                'Empty Cart - Wishlist',
                'Move to Bag'
            )
        }

        try {
            await addItemToNewOrExistingBasket([
                {
                    productId: wishlistItem.productId,
                    quantity: parseInt(wishlistItem.quantity ?? 1, 10)
                }
            ])
            tagManager.addToCartGA4({
                itemsAdded: [{product: pwaProduct}],
                method: 'wishlist',
                currency: globaleCurrency,
                sorting: undefined,
                indexPosition: productIndex,
                listName: 'wishlist',
                listId: 'wishlist'
            })
            await deleteWishlistItem.mutateAsync({
                parameters: {
                    customerId,
                    listId: wishlist.id,
                    itemId: wishlistItem.id
                }
            })

            setRemovedItemIds((prev) => [...prev, wishlistItem.id])

            toast({
                title: formatMessage({
                    defaultMessage: 'Item moved to your bag!',
                    id: 'cart.wishlist.toast.moved_to_bag'
                }),
                status: 'success',
                duration: 3000,
                isClosable: true
            })
        } catch (error) {
            // AC: On failure — error toast shown, item stays in carousel for retry
            console.error('[WishlistInCart] handleMoveToBag failed:', {
                wishlistItemId: wishlistItem?.id,
                productId: wishlistItem?.productId,
                basketId,
                error
            })
            showError()
        } finally {
            setMovingItemIds((prev) => prev.filter((id) => id !== wishlistItem.id))
        }
    }

    const handleViewAllItemsClick = (event) => {
        const viewAllLink =
            event.target.closest('.view-all-wishlist-cta') || event.currentTarget.closest('a')

        if (!viewAllLink) return

        const hasCartItems = Boolean(
            basket?.productItems?.length || basket?.giftCertificateItems?.length
        )
        const ctaText = viewAllLink.textContent.trim() || 'View All Items'

        scheduleIdleAnalytics(() => {
            tagManager.ctaClick(
                'cta_click',
                'Wishlist Interaction',
                `cta click - ${ctaText}`,
                hasCartItems
                    ? `Cart - Wishlist - ${ctaText}`
                    : `Empty Cart - Wishlist - ${ctaText}`,
                ctaText
            )
        })
    }

    if (!isRegistered) {
        return <WishlistGuestPrompt />
    }

    const isDataLoading = isWishlistLoading || (Boolean(productIds) && isProductsLoading)

    if (!isDataLoading && visibleItems.length === 0) {
        return <WishlistEmptyState />
    }

    return (
        <Box {...cartStyles.wishlistInCart.preview} data-testid="sf-cart-wishlist-preview">
            {wishlistCarouselHeadingLoading ? (
                <WishlistHeadingSkeleton />
            ) : wishlistCarouselHeading?.contentAssetHTML ? (
                <Flex
                    className="wishlist-carousel-header"
                    dangerouslySetInnerHTML={{__html: wishlistCarouselHeading.contentAssetHTML}}
                    onClick={handleViewAllItemsClick}
                    sx={{
                        '.wishlist-carousel-heading': {
                            ...cartStyles.wishlistInCart.title
                        },
                        '.view-all-wishlist-cta': {
                            ...cartStyles.wishlistInCart.viewAllLink
                        },
                        '&.wishlist-carousel-header': {
                            justifyContent: {base: 'space-between', lg: 'start'}
                        }
                    }}
                    {...cartStyles.wishlistInCart.titleRow}
                />
            ) : (
                <Flex {...cartStyles.wishlistInCart.titleRow}>
                    <Heading {...cartStyles.wishlistInCart.title}>
                        <FormattedMessage defaultMessage="Your Wishlist" id="cart.wishlist.title" />
                    </Heading>
                    <Box marginTop={2}>
                        <Link
                            href={hostUrl + KS_WISHLIST_URL}
                            {...cartStyles.wishlistInCart.viewAllLink}
                            onClick={handleViewAllItemsClick}
                        >
                            <FormattedMessage
                                defaultMessage="View All Items"
                                id="cart.wishlist.link.view_all"
                            />
                        </Link>
                    </Box>
                </Flex>
            )}

            {isDataLoading ? (
                <Flex gap={4} overflow="hidden">
                    {Array.from({length: 4}).map((_, idx) => (
                        <WishlistItemSkeleton key={`wishlist-skeleton-${idx}`} />
                    ))}
                </Flex>
            ) : (
                <WishlistCarousel
                    items={visibleItems}
                    movingItemIds={movingItemIds}
                    products={products}
                    onMoveToBag={handleMoveToBag}
                />
            )}
        </Box>
    )
}

WishlistInCart.propTypes = {
    content: PropTypes.shape({
        guestHeadline: PropTypes.string,
        guestBody: PropTypes.string,
        carouselTitle: PropTypes.string,
        emptyStateMessage: PropTypes.string
    })
}

WishlistInCart.defaultProps = {
    content: undefined
}

export default WishlistInCart
